import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { SmartImage } from "@/components/SmartImage";
import { Skeleton } from "@/components/ui/skeleton";
import { getScienceIcon, SCIENCE_DISCLAIMER } from "@/data/science";
import { fetchScience } from "@/lib/api";
import { ArrowRight, Hexagon, ShieldCheck, Radio, Leaf } from "lucide-react";

const GROUP_ORDER = ["Technology", "Herbal & Marine"];
const rank = (g) => {
  const i = GROUP_ORDER.indexOf(g);
  return i === -1 ? 99 : i;
};

// Short human label + subtitle per science group (for the grouped landing view).
const GROUP_META = {
  Technology: {
    label: "Wellness wear technology",
    sub: "Graphene at far-infrared (terahertz) na tela para sa comfort, freshness, at recovery-feel.",
  },
  "Herbal & Marine": {
    label: "Herbal & marine ingredients",
    sub: "Mga science-based na sangkap ng elevante Gamat Markisa Plus \u2014 gamat, markisa, at iba pa.",
  },
};

const HIGHLIGHTS = [
  { icon: Hexagon, title: "Even heat & comfort", body: "Ikinakalat ng graphene ang init para pantay at breathable buong araw." },
  { icon: ShieldCheck, title: "Less amoy, mas fresh", body: "Antibacterial / anti-odor na tela para mas malinis ang pakiramdam." },
  { icon: Radio, title: "Recovery-feel (FIR)", body: "Far-infrared na sumusuporta sa circulation-feel at recovery." },
  { icon: Leaf, title: "Herbal & marine", body: "Gamat, passion fruit, at iba pang science-based na sangkap." },
];

export const ScienceBenefits = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    fetchScience()
      .then((d) => mounted && setArticles(Array.isArray(d) ? d : []))
      .catch((err) => {
        console.error("Failed to load science articles:", err);
        if (mounted) setArticles([]);
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const sorted = [...articles].sort((a, b) => rank(a.group) - rank(b.group));

  // Group ALL articles by category so every element (incl. Gamat ingredients) is visible.
  const groupNames = [...new Set(sorted.map((a) => a.group))].sort((a, b) => rank(a) - rank(b));
  const grouped = groupNames
    .map((g) => ({ group: g, items: sorted.filter((a) => a.group === g) }))
    .filter((x) => x.items.length > 0);

  return (
    <section id="science" className="scroll-anchor bg-background" data-testid="science-benefits-section">
      <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:py-20">
        <Reveal>
          <div className="max-w-2xl">
            <p className="text-sm font-semibold uppercase tracking-wider text-accent">Science &amp; Benefits</p>
            <h2 className="font-display mt-2 text-3xl font-semibold tracking-tight text-foreground sm:text-4xl" data-testid="science-benefits-heading">
              Science at benefits sa likod ng bawat product
            </h2>
            <p className="mt-3 text-base leading-relaxed text-muted-foreground">
              Hindi lang basta damit at supplement &mdash; may totoong science: graphene at far-infrared (terahertz) wellness wear,
              plus herbal at marine na sangkap. Ito ang mga benepisyong mararamdaman mo araw-araw.
            </p>
          </div>
        </Reveal>

        {/* Benefit highlights */}
        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {HIGHLIGHTS.map((h, i) => {
            const Icon = h.icon;
            return (
              <Reveal key={h.title} delay={i * 0.05}>
                <div className="flex h-full flex-col rounded-2xl border border-border bg-card p-5" data-testid={`science-highlight-${i}`}>
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent/12 text-accent">
                    <Icon className="h-6 w-6" />
                  </span>
                  <h3 className="mt-4 text-base font-semibold text-foreground">{h.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{h.body}</p>
                </div>
              </Reveal>
            );
          })}
        </div>

        {/* Science element cards */}
        <div className="mt-12">
          <div className="flex items-end justify-between gap-4">
            <h3 className="font-display text-xl font-semibold text-foreground">Ang science sa likod ng bawat infused element</h3>
            <Link
              to="/science"
              className="hidden shrink-0 items-center gap-1.5 text-sm font-medium text-primary hover:underline sm:inline-flex"
              data-testid="science-benefits-viewall-top"
            >
              Basahin lahat <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          {loading ? (
            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-40 w-full rounded-2xl" />
              ))}
            </div>
          ) : grouped.length === 0 ? (
            <p className="mt-5 text-muted-foreground">Wala pang science articles.</p>
          ) : (
            <div className="mt-6 space-y-10">
              {grouped.map((section, gi) => {
                const meta = GROUP_META[section.group] || { label: section.group, sub: "" };
                return (
                  <div key={section.group} data-testid={`science-group-${section.group.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                    <Reveal>
                      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                        <h4 className="font-display text-lg font-semibold text-foreground">{meta.label}</h4>
                        <span className="rounded-full border border-border bg-secondary px-2.5 py-0.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                          {section.items.length} {section.items.length === 1 ? "element" : "elements"}
                        </span>
                      </div>
                      {meta.sub ? (
                        <p className="mt-1 max-w-2xl text-sm leading-relaxed text-muted-foreground">{meta.sub}</p>
                      ) : null}
                    </Reveal>

                    <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {section.items.map((a, i) => {
                        const Icon = getScienceIcon(a.icon || a.name);
                        return (
                          <Reveal key={a.slug} delay={Math.min(i * 0.04, 0.2)}>
                            <Link
                              to={`/science/${a.slug}`}
                              className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-[0_18px_50px_-26px_rgba(15,23,42,0.35)]"
                              data-testid={`science-benefit-card-${a.slug}`}
                            >
                              {a.image ? (
                                <div className="aspect-[16/9] w-full overflow-hidden bg-secondary/40" data-testid={`science-benefit-image-${a.slug}`}>
                                  <SmartImage
                                    src={a.image}
                                    alt={a.name}
                                    fit="cover"
                                    fallbackLabel={a.name}
                                    className="transition-transform duration-300 group-hover:scale-[1.03]"
                                  />
                                </div>
                              ) : null}
                              <div className="flex flex-1 flex-col p-5">
                                <div className="flex items-center gap-3">
                                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/12 text-primary">
                                    <Icon className="h-6 w-6" />
                                  </span>
                                  <span className="rounded-full border border-border px-2.5 py-0.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{a.group}</span>
                                </div>
                                <h5 className="mt-4 text-lg font-semibold text-foreground group-hover:text-primary">{a.name}</h5>
                                <p className="mt-1 flex-1 text-sm leading-relaxed text-muted-foreground">{a.tagline}</p>
                                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary">
                                  Alamin pa <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                                </span>
                              </div>
                            </Link>
                          </Reveal>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-8">
            <Link
              to="/science"
              className="inline-flex items-center gap-2 rounded-xl border border-border bg-secondary px-5 py-3 text-sm font-semibold text-foreground transition-colors duration-200 hover:border-primary/40 hover:text-primary"
              data-testid="science-benefits-viewall"
            >
              Basahin ang buong science ng Ecentra <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <p className="mt-6 rounded-lg border border-border bg-muted/50 p-3 text-xs leading-relaxed text-muted-foreground" data-testid="science-benefits-disclaimer">
            {SCIENCE_DISCLAIMER}
          </p>
        </div>
      </div>
    </section>
  );
};
